import { isInternalRole } from "../../worker/internal-roles";
import type * as React from "react";
import { createFileRoute, Link, useParams, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { fetchApplicationStatus, submitChallenge, uploadChallengeFile, type ChallengeSubmitResult } from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button, buttonClasses } from "@/components/ui/button";
import { Input, Label } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Container } from "@/components/container";
import { AlertCircle, ArrowRight, Copy, Loader2 } from "lucide-react";
import { Markdown } from "@/components/markdown";
import { EagerFileInput, useEagerUpload } from "@/components/eager-file-input";
import { statusFallbackMessage, statusLabel, timelineSteps } from "@/lib/application-status";
import { challengeSubmissionFailure, WRITE_MAINTENANCE_MESSAGE } from "../../shared/submission-messages";

const searchSchema = z.object({
  sid: z.string().optional().default(""),
});

export const Route = createFileRoute("/jobs/$slug/status")({
  validateSearch: searchSchema,
  component: StatusPage,
});

const timelineDateFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatTimelineDate(value: string): string {
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? value : timelineDateFormat.format(ms);
}
function isChallengeStatus(status: { name: string; type: string }): boolean {
  return status.type === "started" && status.name.trim().toLowerCase() === "challenge";
}

function formatChallengeSubmitError(result: ChallengeSubmitResult, submissionId: string): string {
  switch (result.error) {
    case "write_maintenance":
      return WRITE_MAINTENANCE_MESSAGE;
    case "validation":
      return `Some fields didn't validate: ${JSON.stringify(result.issues ?? {})}`;
    case "file_too_large":
      return "Your uploaded file is larger than 10MB. Please attach a smaller file.";
    case "file_type_blocked":
      return "That file type isn't allowed. Upload a PDF, DOC, or similar document.";
    case "upload_missing":
      return "Your uploaded file was missing. Please choose the file again.";
    case "challenge_not_open":
      return "Challenge submission is not open for this application.";
    case "linear_missing":
    case "internal_error":
    default:
      return challengeSubmissionFailure(submissionId);
  }
}

function ChallengeSubmissionForm({ slug, submissionId }: { slug: string; submissionId: string }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submissionKind, setSubmissionKind] = useState<"link" | "file">("link");
  const challengeUpload = useEagerUpload((file, onProgress) => uploadChallengeFile(slug, submissionId, file, onProgress));
  const uploadsBusy = submissionKind === "file" && challengeUpload.status === "uploading";
  const uploadsFailed = submissionKind === "file" && challengeUpload.status === "error";

  function onKindChange(value: string) {
    const nextKind = value === "file" ? "file" : "link";
    setSubmissionKind(nextKind);
    setError(null);
    if (nextKind === "link") challengeUpload.upload(null);
  }

  async function onSubmit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setError(null);
    const fd = new FormData(ev.currentTarget);
    if (submissionKind === "link") {
      const challengeUrl = String(fd.get("challenge_url") ?? "").trim();
      if (!challengeUrl) {
        setError("Add a challenge link.");
        return;
      }
      fd.set("challenge_url", challengeUrl);
      fd.delete("challenge_upload_id");
    } else {
      fd.delete("challenge_url");
      if (challengeUpload.status === "uploading") {
        setError("Wait for the challenge upload to finish.");
        return;
      }
      if (challengeUpload.status === "error") {
        setError("Choose your challenge file again.");
        return;
      }
      if (challengeUpload.status !== "ready") {
        setError("Upload a challenge file.");
        return;
      }
      fd.set("challenge_upload_id", challengeUpload.uploadId);
    }
    setSubmitting(true);
    try {
      const result = await submitChallenge(slug, submissionId, fd);
      if (result.ok) {
        setSubmitted(true);
        return;
      }
      setError(formatChallengeSubmitError(result, submissionId));
    } catch {
      setError(challengeSubmissionFailure(submissionId));
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Alert>
        <AlertTitle>Challenge submitted</AlertTitle>
        <AlertDescription>Your challenge submission has been attached to the Linear issue.</AlertDescription>
      </Alert>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-6 space-y-8 rounded-md border bg-background p-4">
      <section>
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Challenge submission</p>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          Choose one submission method. A challenge link or challenge file is required.
        </p>
      </section>

      <Tabs value={submissionKind} onValueChange={onKindChange}>
        <TabsList className="grid w-full grid-cols-2 sm:w-fit">
          <TabsTrigger value="link">Link</TabsTrigger>
          <TabsTrigger value="file">File upload</TabsTrigger>
        </TabsList>

        <TabsContent value="link" className="mt-1">
          <div className="space-y-2">
            <Label htmlFor="challenge_url">Challenge link</Label>
            <Input id="challenge_url" name="challenge_url" type="url" placeholder="https://github.com/you/project" maxLength={1000} required />
          </div>
        </TabsContent>

        <TabsContent value="file" className="mt-1">
          <EagerFileInput
            id="challenge_file"
            label={<>Challenge file <span className="font-normal text-muted-foreground">(required, up to 10MB)</span></>}
            upload={challengeUpload}
            required
            disabled={submitting}
          />
        </TabsContent>
      </Tabs>

      {error && (
        <Alert variant="destructive">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>Submission failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button type="submit" size="lg" disabled={submitting || uploadsBusy || uploadsFailed}>
          {submitting || uploadsBusy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <ArrowRight aria-hidden="true" />}
          {submitting ? "Submitting…" : uploadsBusy ? "Uploading…" : "Submit challenge"}
        </Button>
      </div>
    </form>
  );
}

function StatusPage() {
  const { slug } = useParams({ from: "/jobs/$slug/status" });
  const { sid } = useSearch({ from: "/jobs/$slug/status" });
  const statusQuery = useQuery({
    queryKey: ["application-status", slug, sid],
    queryFn: () => fetchApplicationStatus(slug, sid),
    enabled: sid.length > 0,
    refetchInterval: (query) => query.state.status !== "error" && query.state.data?.linear_pending ? 15_000 : false,
    retry: (failureCount, error) => {
      const status = (error as { status?: number }).status;
      return status !== 404 && failureCount < 2;
    },
  });

  const internal = isInternalRole({ slug }) || statusQuery.data?.job_publicly_visible === false;
  const statusPath = `/jobs/${encodeURIComponent(slug)}/status?sid=${encodeURIComponent(sid)}`;
  const statusLink = typeof window === "undefined" ? statusPath : new URL(statusPath, window.location.origin).toString();
  const [copied, setCopied] = useState(false);

  async function copyStatusLink() {
    await navigator.clipboard.writeText(statusLink);
    setCopied(true);
  }

  if (!sid) {
    return (
      <Container width="lg" className="py-20">
        <Alert variant="destructive">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>Application status</AlertTitle>
          <AlertDescription>This status link is missing a submission id.</AlertDescription>
        </Alert>
        <Link to={internal ? "/" : "/jobs/$slug"} params={{ slug }} className={buttonClasses({ variant: "outline", className: "mt-6" })}>
          {internal ? "Back to jobs" : "Back to role"}
        </Link>
      </Container>
    );
  }

  return (
    <Container width="lg" className="py-12">
      <Link to={internal ? "/" : "/jobs/$slug"} params={{ slug }} className="text-sm text-muted-foreground hover:text-foreground">
        ← {internal ? "Back to jobs" : "Back to role"}
      </Link>

      <header className="mt-6 mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Application status</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            {statusQuery.data && (
              <span>
                Applicant <span className="font-medium text-foreground">{statusQuery.data.applicant_name}</span>
              </span>
            )}
            <span>
              Submission <span className="font-mono">{sid}</span>
            </span>
            <Button type="button" variant="ghost" size="sm" onClick={copyStatusLink} className="h-auto shrink-0 px-2 py-1">
              <Copy aria-hidden="true" />
              {copied ? "Copied" : "Copy link"}
            </Button>
          </div>
        </div>
      </header>

      <div className="mt-8">
        {statusQuery.isLoading && (
          <div className="grid gap-8 lg:grid-cols-[minmax(12rem,16rem)_1fr] lg:items-start">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        )}

        {statusQuery.isError && (
          <Alert variant="destructive">
            <AlertCircle aria-hidden="true" />
            <AlertTitle>We couldn't load this status.</AlertTitle>
            <AlertDescription>
              Check the link, or email your resume to hiring@bytebeam.io with the job title in the subject and your submission id.
            </AlertDescription>
          </Alert>
        )}

        {statusQuery.data && (
          <div className="grid gap-8 lg:grid-cols-[minmax(12rem,16rem)_1fr] lg:items-start">
            <section>
              <h2 className="text-lg font-semibold mb-4">Timeline</h2>
              <ol className="relative ml-3 space-y-6 border-l">
                {timelineSteps(statusQuery.data).map((entry) => (
                  <li key={entry.type} className="relative pl-6">
                    <span
                      className={
                        entry.active
                          ? "absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary ring-4 ring-accent"
                          : "absolute -left-[4px] top-1.5 h-2 w-2 rounded-full bg-muted-foreground/40"
                      }
                    />
                    <p className={entry.active ? "font-semibold leading-none" : "font-medium leading-none text-muted-foreground"}>
                      {entry.name}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {entry.at ? formatTimelineDate(entry.at) : "Not reached"}
                    </p>
                  </li>
                ))}
              </ol>
            </section>

            <section className="min-w-0">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold">Current status</h2>
                <Badge variant="outline" className="bg-background font-normal">
                  {statusQuery.data.linear_pending ? "Application received" : statusLabel(statusQuery.data.status)}
                </Badge>
              </div>
              <div className="rounded-md border bg-background p-4 text-sm text-muted-foreground">
                {statusQuery.data.linear_pending ? "Application received; being processed. Your resume is saved. You do not need to apply again." : statusQuery.data.status.description_md
                  ? <Markdown>{statusQuery.data.status.description_md}</Markdown>
                  : statusFallbackMessage(statusQuery.data.status)}
              </div>
              {!statusQuery.data.linear_pending && isChallengeStatus(statusQuery.data.status) && (
                <ChallengeSubmissionForm slug={slug} submissionId={statusQuery.data.submission_id} />
              )}
            </section>
          </div>
        )}
      </div>
    </Container>
  );
}
